<script setup lang="ts">
import { useI18n } from '@/i18n'

const { t } = useI18n()
const currentYear = new Date().getFullYear()
</script>

<template>
  <footer class="app-footer">
    <div class="footer-content">
      <div class="footer-main">
        <!-- Brand - Left -->
        <div class="footer-brand">
          <router-link to="/" class="logo">
            <img src="/official_logo_128.png" alt="M2V.io" class="logo-img" />
            <span class="logo-text">M2V.io</span>
          </router-link>
          <p class="tagline">{{ t('footer.tagline') }}</p>
          <p class="copyright">&copy; {{ currentYear }} M2V.io</p>
        </div>
        
        <!-- Links - Right -->
        <div class="footer-links">
          <div class="link-group">
            <h4>{{ t('footer.navigation') }}</h4>
            <router-link to="/">{{ t('nav.home') }}</router-link>
            <router-link to="/gallery">{{ t('nav.gallery') }}</router-link>
            <router-link to="/pricing">{{ t('nav.pricing') }}</router-link>
          </div>
          
          <div class="link-group">
            <h4>{{ t('footer.support') }}</h4>
            <router-link to="/faq">{{ t('footer.faq') }}</router-link>
            <router-link to="/contact">{{ t('footer.contact') }}</router-link>
          </div>
          
          <div class="link-group">
            <h4>{{ t('footer.legal') }}</h4>
            <router-link to="/terms">{{ t('footer.terms') }}</router-link>
            <router-link to="/privacy">{{ t('footer.privacy') }}</router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.app-footer {
  background: var(--m2v-bg-surface);
  border-top: 1px solid var(--m2v-border-subtle);
  padding: var(--m2v-space-8) var(--m2v-space-6);
  margin-top: auto;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--m2v-space-8);
}

.footer-brand {
  flex-shrink: 0;
}

.logo {
  display: inline-flex;
  align-items: center;
  gap: var(--m2v-space-2);
  font-family: var(--m2v-font-display);
  font-size: 1.25rem;
}

.logo-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  font-weight: 700;
  color: var(--m2v-text-primary);
  text-decoration: none;
  margin-bottom: var(--m2v-space-2);
}

.logo-text {
  background: var(--m2v-gradient-hero);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tagline {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
  max-width: 250px;
  line-height: 1.5;
  margin-bottom: var(--m2v-space-3);
}

.copyright {
  color: var(--m2v-text-muted);
  font-size: 0.75rem;
}

.footer-links {
  display: flex;
  gap: var(--m2v-space-12);
}

.link-group {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-2);
}

.link-group h4 {
  color: var(--m2v-text-primary);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--m2v-space-1);
  font-weight: 600;
}

.link-group a {
  color: var(--m2v-text-muted);
  font-size: 0.875rem;
  transition: color var(--m2v-transition-fast);
}

.link-group a:hover {
  color: var(--m2v-primary-light);
}

@media (max-width: 640px) {
  .footer-main {
    flex-direction: column;
    gap: var(--m2v-space-6);
  }
  
  .footer-links {
    gap: var(--m2v-space-8);
  }
}
</style>
